import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AudienceSpec, Form, FormHandling, FormList, Group } from '@up2d8/contracts';
import { problemDetail } from '@up2d8/api-client';
import {
  AdminIcon,
  AdminPage,
  Badge,
  Btn,
  Card,
  Chip,
  Drawer,
  Empty,
  Field,
  IconBtn,
  Input,
  Note,
  PageHead,
  Radio,
  Select,
  Textarea,
} from '../admin-kit';
import { AudienceField } from '../beheer-communicatie/audience-field';
import { LoadError, Loading } from '../beheer-communicatie/page-state';
import { useSession } from '../../shared/session';
import { useProblemText } from '../../shared/use-problem-text';
import { ConfirmModal } from '../organisatie/confirm-modal';
import { audienceIncomplete } from '../../shared/audience-preview';

/**
 * De formulieren als lijst (Stap 37, nieuw ontwerp in BHR-5, frame C5).
 *
 * Eén rij per formulier, met de versie die live staat en hoeveel aanvragen er openstaan. Een rij
 * opent de bouwer op zijn eigen adres (C6, `form-builder-page.tsx`); "Nieuw formulier" opent een
 * paneel, en na het aanmaken ga je meteen naar de bouwer om er velden op te zetten.
 */
export function FormsListPage() {
  const { t } = useTranslation();
  const { api } = useSession();
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const queryClient = useQueryClient();
  const problemText = useProblemText();
  const [teVerwijderen, setTeVerwijderen] = useState<Form | null>(null);
  const [fout, setFout] = useState<string | null>(null);

  // De acties van berichten, ook bij formulieren (FBK-7): dupliceren, archiveren, verwijderen.
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['forms'] });
  const duplicate = useMutation({
    mutationFn: async (formId: string) => {
      const { data, error } = await api.POST('/api/v1/forms/{formId}/duplicate', {
        params: { path: { formId } },
      });
      if (error || !data) throw error ?? new Error('Dupliceren mislukt');
      return data as Form;
    },
    onSuccess: (form) => {
      void refresh();
      void navigate({ to: '/admin/formulieren/$formId', params: { formId: form.id } });
    },
    onError: (error) => setFout(problemText(error, t('errors.generic'))),
  });
  const archive = useMutation({
    mutationFn: async (input: { formId: string; archived: boolean }) => {
      const { error } = await api.POST('/api/v1/forms/{formId}/archive', {
        params: { path: { formId: input.formId } },
        body: { archived: input.archived } as never,
      });
      if (error) throw error;
    },
    onSuccess: () => void refresh(),
    onError: (error) => setFout(problemText(error, t('errors.generic'))),
  });
  const remove = useMutation({
    mutationFn: async (formId: string) => {
      const { error } = await api.DELETE('/api/v1/forms/{formId}', {
        params: { path: { formId } },
      });
      if (error) throw error;
    },
    onSuccess: () => {
      setTeVerwijderen(null);
      void refresh();
    },
    onError: (error) => {
      setTeVerwijderen(null);
      setFout(problemText(error, t('errors.generic')));
    },
  });

  const forms = useQuery({
    queryKey: ['forms'],
    queryFn: async (): Promise<Form[]> => {
      const { data, error } = await api.GET('/api/v1/forms', {});
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return (data as FormList).items;
    },
  });

  const items = forms.data ?? [];

  return (
    <AdminPage>
      <PageHead
        title={t('formsAdmin.title')}
        sub={t('beheer.communicatie.forms.sub')}
        actions={
          <Btn icon={<AdminIcon name="plus" />} testId="form-new" onClick={() => setCreating(true)}>
            {t('formsAdmin.newTitle')}
          </Btn>
        }
      />

      {fout ? (
        <Note tone="danger" role="alert" testId="page-error">
          {fout}
        </Note>
      ) : null}

      <Card label={t('formsAdmin.title')}>
        {forms.isPending ? (
          <Loading />
        ) : forms.isError ? (
          <LoadError onRetry={() => void forms.refetch()} error={forms.error} />
        ) : items.length === 0 ? (
          <Empty
            icon={<AdminIcon name="clipboardList" size={24} />}
            title={t('formsAdmin.emptyTitle')}
            text={t('beheer.communicatie.forms.emptyHint')}
          />
        ) : (
          <ul className="c-list">
            {items.map((form) => (
              <li key={form.id} style={{ display: 'flex', alignItems: 'center' }}>
                <Link
                  style={{ flex: 1, minWidth: 0, opacity: form.archived ? 0.6 : undefined }}
                  to="/admin/formulieren/$formId"
                  params={{ formId: form.id }}
                  className="a-item c-row-link"
                  data-testid="form-row"
                >
                  <span className="a-tile" aria-hidden="true">
                    <AdminIcon name="clipboardList" size={20} />
                  </span>
                  <div className="a-item-info">
                    <div className="a-item-top">
                      <span className="a-name">{form.title}</span>
                      {form.publishedVersion ? (
                        <Badge tone="navy">
                          {t('formsAdmin.versionLive', {
                            number: form.publishedVersion.versionNumber,
                          })}
                        </Badge>
                      ) : (
                        <Badge>{t('formsAdmin.notPublished')}</Badge>
                      )}
                      {form.draftVersion ? <Badge>{t('formsAdmin.hasDraft')}</Badge> : null}
                      {form.archived ? <Badge>{t('formsManage.archived')}</Badge> : null}
                      {/* Een actief formulier waarvan geen behandelgroep meer bestaat (hertest 29 okt.). */}
                      {form.handlerMissing && !form.archived ? (
                        <Badge tone="danger">{t('formsAdmin.handlerMissing')}</Badge>
                      ) : null}
                      {form.openSubmissions > 0 ? (
                        <Badge tone="solid">
                          {t('formsAdmin.openCount', { count: form.openSubmissions })}
                        </Badge>
                      ) : null}
                    </div>
                    <div className="a-meta">
                      {form.audienceDescription} · {handlingText(t, form)}
                    </div>
                  </div>
                  <AdminIcon name="chevronRight" size={18} className="c-chevron" />
                </Link>
                <div className="a-actions" style={{ flexWrap: 'nowrap', paddingRight: 12 }}>
                  <IconBtn
                    label={t('formsManage.duplicate')}
                    icon={<AdminIcon name="copy" />}
                    testId="form-duplicate"
                    onClick={() => duplicate.mutate(form.id)}
                  />
                  <IconBtn
                    label={form.archived ? t('formsManage.restore') : t('formsManage.archive')}
                    icon={<AdminIcon name={form.archived ? 'eye' : 'eyeOff'} />}
                    testId="form-archive"
                    onClick={() => archive.mutate({ formId: form.id, archived: !form.archived })}
                  />
                  {/*
                    Verwijderen alleen zonder inzendingen: een knop die altijd weigert, leert mensen
                    knoppen negeren (zoals bij berichten). Met inzendingen archiveer je. Het totaal
                    over alle versies: ook een aanvraag op een oudere versie telt (#10).
                  */}
                  {form.submissionCount === 0 ? (
                    <IconBtn
                      label={t('common.delete')}
                      icon={<AdminIcon name="trash" />}
                      testId="form-delete"
                      onClick={() => setTeVerwijderen(form)}
                    />
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <ConfirmModal
        open={teVerwijderen !== null}
        onClose={() => setTeVerwijderen(null)}
        title={t('formsManage.deleteTitle')}
        description={t('formsManage.deleteText', { title: teVerwijderen?.title ?? '' })}
        confirmLabel={t('common.delete')}
        busy={remove.isPending}
        testId="confirm-form-delete"
        onConfirm={() => teVerwijderen && remove.mutate(teVerwijderen.id)}
      />

      <NewFormDrawer
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={(form) => {
          setCreating(false);
          void navigate({ to: '/admin/formulieren/$formId', params: { formId: form.id } });
        }}
      />
    </AdminPage>
  );
}

/** "Behandeld door Logistiek" of "Alleen verzamelen". */
export function handlingText(
  t: (key: string, options?: Record<string, unknown>) => string,
  form: Form,
) {
  if (form.handling === 'group') {
    // Meerdere groepen sinds FBK-7: "Behandeld door Ploeg A of HR".
    const names =
      form.handlerGroupNames.length > 0 ? form.handlerGroupNames : [form.handlerGroupName ?? '?'];
    return t('formsAdmin.handledBy', { name: names.join(` ${t('formsManage.or')} `) });
  }
  // De eigen leidinggevende (Stap 17): geen vaste groep, één behandelaar per aanvraag.
  if (form.handling === 'manager') return t('beheer.communicatie.forms.handledByManager');
  return t('formsAdmin.noHandling');
}

/** De groepen die een formulier kunnen behandelen. */
export function useGroups() {
  const { api } = useSession();
  return useQuery({
    queryKey: ['groups'],
    queryFn: async (): Promise<Group[]> => {
      const { data } = await api.GET('/api/v1/groups');
      return ((data as { items: Group[] } | undefined)?.items ?? []) as Group[];
    },
  });
}

/** Een nieuw formulier: titel, doelgroep en wie behandelt. De velden komen daarna, in de bouwer. */
function NewFormDrawer({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (form: Form) => void;
}) {
  const { t } = useTranslation();
  const { api } = useSession();
  const queryClient = useQueryClient();
  const groups = useGroups();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [audience, setAudience] = useState<AudienceSpec>({ type: 'all' });
  const [handling, setHandling] = useState<FormHandling>('none');
  const [handlerGroupIds, setHandlerGroupIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: async () => {
      const { data, error: failed } = await api.POST('/api/v1/forms', {
        body: {
          title,
          description,
          audience,
          handling,
          handlerGroupId: null,
          handlerGroupIds: handling === 'group' ? handlerGroupIds : [],
        },
      });
      if (failed || !data) throw failed ?? new Error('Opslaan mislukt');
      return data as Form;
    },
    onSuccess: (form) => {
      void queryClient.invalidateQueries({ queryKey: ['forms'] });
      setTitle('');
      setDescription('');
      setError(null);
      onCreated(form);
    },
    onError: (failed) => setError(problemDetail(failed) ?? t('errors.generic')),
  });

  // Ook geen lege doelgroep: "Kiezen" zonder keuze weigert de server (hertest 30 okt.).
  const complete =
    title.trim() !== '' &&
    !(handling === 'group' && handlerGroupIds.length === 0) &&
    !audienceIncomplete(audience);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={t('formsAdmin.newTitle')}
      testId="form-new-drawer"
      footer={
        <>
          <Btn variant="sec" onClick={onClose}>
            {t('common.cancel')}
          </Btn>
          <Btn
            type="submit"
            form="form-new-form"
            icon={<AdminIcon name="plus" />}
            disabled={!complete || create.isPending}
            testId="form-create"
          >
            {t('formsAdmin.create')}
          </Btn>
        </>
      }
    >
      <form
        id="form-new-form"
        className="a-form"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          if (complete && !create.isPending) create.mutate();
        }}
      >
        <Field label={t('formsAdmin.formTitle')}>
          {(props) => (
            <Input
              {...props}
              value={title}
              maxLength={120}
              data-testid="form-title"
              onChange={(event) => setTitle(event.target.value)}
            />
          )}
        </Field>
        <Field label={t('formsAdmin.formDescription')} optional>
          {(props) => (
            <Textarea
              {...props}
              value={description}
              maxLength={500}
              data-testid="form-description"
              onChange={(event) => setDescription(event.target.value)}
            />
          )}
        </Field>

        <AudienceField value={audience} onChange={setAudience} testId="form-audience" />

        <HandlingFields
          name="form-new-handling"
          handling={handling}
          onHandling={setHandling}
          groupIds={handlerGroupIds}
          onGroup={setHandlerGroupIds}
          groups={groups.data ?? []}
          groupTestId="form-handler-group"
        />

        {error ? (
          <Note tone="danger" role="alert">
            {error}
          </Note>
        ) : null}
      </form>
    </Drawer>
  );
}

/**
 * "Wie behandelt de aanvragen?" als twee keuzerondjes, en bij "Een groep" de groep (C6). Ook in
 * het paneel "Nieuw formulier", zodat de keuze er op beide plekken hetzelfde uitziet.
 */
export function HandlingFields({
  name,
  handling,
  onHandling,
  groupIds,
  onGroup,
  groups,
  groupTestId,
}: {
  name: string;
  handling: FormHandling;
  onHandling: (handling: FormHandling) => void;
  /** Eén of meer behandelgroepen (FBK-7); een lid van eender welke beslist. */
  groupIds: string[];
  onGroup: (groupIds: string[]) => void;
  groups: Group[];
  groupTestId: string;
}) {
  const { t } = useTranslation();
  return (
    <>
      <fieldset className="a-field c-fieldset">
        <legend className="a-label">{t('formsAdmin.handling')}</legend>
        <Radio
          name={name}
          value="none"
          checked={handling === 'none'}
          onChange={() => onHandling('none')}
          label={t('beheer.communicatie.forms.handlingNone')}
        />
        <Radio
          name={name}
          value="group"
          checked={handling === 'group'}
          onChange={() => onHandling('group')}
          label={t('formsAdmin.handlingGroup')}
        />
        <Radio
          name={name}
          value="manager"
          checked={handling === 'manager'}
          onChange={() => onHandling('manager')}
          label={t('beheer.communicatie.forms.handlingManager')}
          hint={t('beheer.communicatie.forms.handlingManagerHint')}
        />
        <div className="a-hint">{t('formsAdmin.handlingHint')}</div>
      </fieldset>
      {handling === 'group' ? (
        <Field
          label={t('beheer.communicatie.forms.group')}
          hint={groupIds.length > 1 ? t('formsManage.groupsHint') : undefined}
        >
          {(props) => (
            <div style={{ display: 'grid', gap: 8 }}>
              {groupIds.length > 0 ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {groupIds.map((id) => (
                    <Chip
                      key={id}
                      testId="form-handler-group-chip"
                      onRemove={() => onGroup(groupIds.filter((other) => other !== id))}
                    >
                      {groups.find((group) => group.id === id)?.name ?? '…'}
                    </Chip>
                  ))}
                </div>
              ) : null}
              <Select
                {...props}
                value=""
                data-testid={groupTestId}
                onChange={(event) => {
                  if (event.target.value) onGroup([...groupIds, event.target.value]);
                }}
              >
                <option value="">
                  {groupIds.length === 0 ? t('forms.choose') : t('formsManage.addGroup')}
                </option>
                {groups
                  .filter((group) => !groupIds.includes(group.id))
                  .map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.name}
                    </option>
                  ))}
              </Select>
            </div>
          )}
        </Field>
      ) : null}
    </>
  );
}
